import { forwardRef, useState } from "react";
import type { VariantProps } from "class-variance-authority";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";
import { cn } from "@/lib/cn";
import { textInputVariants } from "./text-input";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "./surface-contract";

/**
 * PasswordInput — reuses the shared Tahoe input recipe (textInputVariants).
 *
 * Same glass input style as TextInput (ui/input-default, ui/input-focused),
 * with extra trailing padding for the visibility toggle. No duplicate recipe.
 * Defaults to the "field" shape (passwords live in forms).
 */
type PasswordInputProps = Omit<React.ComponentProps<"input">, "type"> &
  VariantProps<typeof textInputVariants> & {
    "data-testid"?: string;
  };

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, "data-testid": testId, shape, ...props }, ref) => {
    const t = useT(uiMessages);
    const [visible, setVisible] = useState(false);

    return (
      <div className="relative">
        <input
          ref={ref}
          {...props}
          type={visible ? "text" : "password"}
          className={cn(
            surfaceRoleClassName("input"),
            textInputVariants({ shape }),
            "pr-appkit-control-icon",
            className,
          )}
          data-testid={testId}
          {...surfaceRoleAttributes("input", { nativeLevel: "child" })}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-appkit-sm top-1/2 -translate-y-1/2 text-muted hover:text-secondary"
          aria-label={visible ? t("hidePassword") : t("showPassword")}
          data-testid="password-input-toggle"
        >
          {visible ? (
            <Icons.EyeOff className="h-3.5 w-3.5" />
          ) : (
            <Icons.Eye className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
export type { PasswordInputProps };
